"use client";

/**
 * useOrderTimeAwareness — keeps the order's fulfillment time honest in real time.
 *
 * Re-evaluates the cart's ASAP/scheduled time every 30s (and on demand) so the
 * displayed "ready by ~" estimate tracks the moving clock, and a scheduled slot
 * that PASSES (or drops below the minimum lead time, or a restaurant that has
 * since closed) is flagged so the UI can warn the customer and block checkout —
 * instead of quietly handing a stale order to the server.
 */

import { useEffect, useMemo, useState } from 'react';
import { type ILocation } from '@order-up/models';
import {
    evaluateOrderTime,
    type OrderTimeEvaluation,
} from '../components/ordering/schedulingUtils';
import { type TimeFormatSettings } from '../utils/timeFormatter';

export function useOrderTimeAwareness(
    locationRecord: ILocation | null | undefined,
    scheduledTime: Date | undefined,
    settings?: TimeFormatSettings,
): OrderTimeEvaluation {
    // A ticking clock — re-evaluate every 30s without calling Date during render.
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    return useMemo(
        () => evaluateOrderTime(locationRecord, scheduledTime, now, settings),
        [locationRecord, scheduledTime, now, settings],
    );
}

export type { OrderTimeEvaluation, OrderTimeStatus } from '../components/ordering/schedulingUtils';
